import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import {
  CreditCard,
  Plus,
  Trash2,
  Upload,
  FileText,
  DollarSign,
  Calendar as CalendarIcon,
  Building2,
  LogOut,
  Phone,
  Mail,
  CheckCircle2,
  Clock,
  AlertCircle,
  User
} from "lucide-react";

const LOGO_URL = "/logo.png";

const CARD_TYPES = ["Visa", "Mastercard", "American Express", "Discover", "Capital One", "Chase", "Citi", "Other"];
const CREDITOR_TYPES = ["Collection Agency", "Medical Debt", "Personal Loan", "Auto Loan", "Student Loan", "Other"];

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  
  // State
  const [creditCards, setCreditCards] = useState([]);
  const [creditors, setCreditors] = useState([]);
  const [creditReports, setCreditReports] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [showCardForm, setShowCardForm] = useState(false);
  const [showCreditorForm, setShowCreditorForm] = useState(false);
  const [cardForm, setCardForm] = useState({
    card_type: "",
    card_issuer: "",
    balance: "",
    credit_limit: "",
    next_payment_date: null,
    minimum_payment: ""
  });
  const [creditorForm, setCreditorForm] = useState({
    creditor_name: "",
    amount_owed: "",
    account_number: "",
    creditor_type: ""
  });
  const [uploading, setUploading] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [cardsRes, creditorsRes, reportsRes, planRes] = await Promise.all([
        axios.get(`${API}/credit-cards`, { headers: authHeaders }),
        axios.get(`${API}/creditors`, { headers: authHeaders }),
        axios.get(`${API}/credit-reports`, { headers: authHeaders }),
        axios.get(`${API}/my-plan`, { headers: authHeaders })
      ]);
      setCreditCards(cardsRes.data);
      setCreditors(creditorsRes.data);
      setCreditReports(reportsRes.data);
      setPlan(planRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculations
  const totalCardDebt = creditCards.reduce((sum, card) => sum + (card.balance || 0), 0);
  const totalCreditorDebt = creditors.reduce((sum, c) => sum + (c.amount_owed || 0), 0);
  const totalDebt = totalCardDebt + totalCreditorDebt;
  const totalCreditLimit = creditCards.reduce((sum, card) => sum + (card.credit_limit || 0), 0);
  const utilizationRate = totalCreditLimit > 0 ? (totalCardDebt / totalCreditLimit) * 100 : 0;

  // Handlers
  const handleAddCard = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...cardForm,
        balance: parseFloat(cardForm.balance),
        credit_limit: parseFloat(cardForm.credit_limit),
        minimum_payment: cardForm.minimum_payment ? parseFloat(cardForm.minimum_payment) : null,
        next_payment_date: cardForm.next_payment_date ? format(cardForm.next_payment_date, "yyyy-MM-dd") : null
      };
      await axios.post(`${API}/credit-cards`, data, { headers: authHeaders });
      toast.success("Credit card added successfully!");
      setShowCardForm(false);
      setCardForm({ card_type: "", card_issuer: "", balance: "", credit_limit: "", next_payment_date: null, minimum_payment: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to add credit card");
    }
  };

  const handleDeleteCard = async (cardId) => {
    try {
      await axios.delete(`${API}/credit-cards/${cardId}`, { headers: authHeaders });
      toast.success("Credit card removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove credit card");
    }
  };

  const handleAddCreditor = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...creditorForm,
        amount_owed: parseFloat(creditorForm.amount_owed)
      };
      await axios.post(`${API}/creditors`, data, { headers: authHeaders });
      toast.success("Creditor added successfully!");
      setShowCreditorForm(false);
      setCreditorForm({ creditor_name: "", amount_owed: "", account_number: "", creditor_type: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to add creditor");
    }
  };

  const handleDeleteCreditor = async (creditorId) => {
    try {
      await axios.delete(`${API}/creditors/${creditorId}`, { headers: authHeaders });
      toast.success("Creditor removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove creditor");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Only PDF files are allowed");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/credit-reports/upload`, formData, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" }
      });
      toast.success("Credit report uploaded successfully!");
      fetchData();
    } catch (error) {
      toast.error("Failed to upload credit report");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    try {
      await axios.delete(`${API}/credit-reports/${reportId}`, { headers: authHeaders });
      toast.success("Credit report deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete credit report");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Credit2Cash" className="h-14 logo-glow" data-testid="dashboard-logo" />
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 text-[#A1A1AA]">
              <User className="w-4 h-4" />
              <span>{user?.full_name}</span>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="stat-card" data-testid="total-debt-stat">
            <p className="text-sm text-[#71717A] font-mono uppercase tracking-wider mb-2">Total Debt</p>
            <p className="text-3xl font-bold money-value">${totalDebt.toLocaleString()}</p>
          </div>
          <div className="stat-card" data-testid="credit-limit-stat">
            <p className="text-sm text-[#71717A] font-mono uppercase tracking-wider mb-2">Total Credit Limit</p>
            <p className="text-3xl font-bold text-[#00F0FF]">${totalCreditLimit.toLocaleString()}</p>
          </div>
          <div className="stat-card" data-testid="utilization-stat">
            <p className="text-sm text-[#71717A] font-mono uppercase tracking-wider mb-2">Utilization Rate</p>
            <p className={`text-3xl font-bold ${utilizationRate > 70 ? 'text-[#FF007F]' : utilizationRate > 30 ? 'text-[#FFD700]' : 'text-[#00FF66]'}`}>
              {utilizationRate.toFixed(1)}%
            </p>
          </div>
          <div className="stat-card" data-testid="plan-status-stat">
            <p className="text-sm text-[#71717A] font-mono uppercase tracking-wider mb-2">Plan Status</p>
            {plan ? (
              <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold status-${plan.status}`}>
                {plan.status === 'pending' && <Clock className="w-4 h-4" />}
                {plan.status === 'active' && <CheckCircle2 className="w-4 h-4" />}
                {plan.status === 'completed' && <CheckCircle2 className="w-4 h-4" />}
                {plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 text-[#71717A]">
                <AlertCircle className="w-4 h-4" />
                Pending Review
              </span>
            )}
          </div>
        </div>

        {/* Plan Info (if exists) */}
        {plan && (
          <div className="premium-card rounded-2xl p-6 mb-8 border-l-4 border-[#00FF66]" data-testid="plan-details">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#00FF66]" />
              Your Credit2Cash Plan
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-[#71717A] mb-1">Mailing Address</p>
                <p className="text-white">{plan.mailing_address}</p>
              </div>
              <div>
                <p className="text-sm text-[#71717A] mb-1">Available Credit Limit</p>
                <p className="text-2xl font-bold money-value">${plan.available_credit_limit?.toLocaleString()}</p>
              </div>
              {plan.notes && (
                <div className="md:col-span-2">
                  <p className="text-sm text-[#71717A] mb-1">Notes</p>
                  <p className="text-[#A1A1AA]">{plan.notes}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Credit Cards Section */}
          <section className="premium-card rounded-2xl p-6" data-testid="credit-cards-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#FFD700]" />
                Credit Cards
              </h2>
              <Button
                onClick={() => setShowCardForm(!showCardForm)}
                className="bg-[#00FF66] text-black font-bold hover:bg-[#00FF66]/90"
                data-testid="add-card-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Card
              </Button>
            </div>

            {/* Add Card Form */}
            {showCardForm && (
              <form onSubmit={handleAddCard} className="bg-black/30 rounded-xl p-4 mb-6 space-y-4" data-testid="card-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white text-sm">Card Type</Label>
                    <Select value={cardForm.card_type} onValueChange={(v) => setCardForm({ ...cardForm, card_type: v })}>
                      <SelectTrigger className="input-dark mt-1" data-testid="card-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {CARD_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white text-sm">Card Issuer (Bank)</Label>
                    <Input
                      value={cardForm.card_issuer}
                      onChange={(e) => setCardForm({ ...cardForm, card_issuer: e.target.value })}
                      placeholder="e.g., Chase, Wells Fargo"
                      className="input-dark mt-1"
                      required
                      data-testid="card-issuer-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white text-sm">Current Balance</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                      <Input
                        type="number"
                        step="0.01"
                        value={cardForm.balance}
                        onChange={(e) => setCardForm({ ...cardForm, balance: e.target.value })}
                        placeholder="0.00"
                        className="pl-8 input-dark"
                        required
                        data-testid="card-balance-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white text-sm">Credit Limit</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                      <Input
                        type="number"
                        step="0.01"
                        value={cardForm.credit_limit}
                        onChange={(e) => setCardForm({ ...cardForm, credit_limit: e.target.value })}
                        placeholder="0.00"
                        className="pl-8 input-dark"
                        required
                        data-testid="card-limit-input"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white text-sm">Next Payment Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full mt-1 input-dark justify-start text-left font-normal"
                          data-testid="payment-date-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-[#71717A]" />
                          {cardForm.next_payment_date ? format(cardForm.next_payment_date, "PPP") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#111] border-white/10">
                        <Calendar
                          mode="single"
                          selected={cardForm.next_payment_date}
                          onSelect={(date) => setCardForm({ ...cardForm, next_payment_date: date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-white text-sm">Minimum Payment</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                      <Input
                        type="number"
                        step="0.01"
                        value={cardForm.minimum_payment}
                        onChange={(e) => setCardForm({ ...cardForm, minimum_payment: e.target.value })}
                        placeholder="0.00"
                        className="pl-8 input-dark"
                        data-testid="card-min-payment-input"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="bg-[#00FF66] text-black font-bold" data-testid="save-card-btn">
                    Save Card
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowCardForm(false)} className="border-white/20 text-white">
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* Cards List */}
            <div className="space-y-3">
              {creditCards.length === 0 ? (
                <p className="text-center text-[#71717A] py-8">No credit cards added yet</p>
              ) : (
                creditCards.map((card) => (
                  <div key={card.id} className="bg-black/30 rounded-xl p-4 flex items-center justify-between" data-testid={`card-item-${card.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CreditCard className="w-4 h-4 text-[#FFD700]" />
                        <span className="font-semibold text-white">{card.card_type}</span>
                        <span className="text-[#71717A]">• {card.card_issuer}</span>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <span className="text-[#FF007F]">Balance: ${card.balance?.toLocaleString()}</span>
                        <span className="text-[#00F0FF]">Limit: ${card.credit_limit?.toLocaleString()}</span>
                        {card.next_payment_date && (
                          <span className="text-[#A1A1AA]">Due: {card.next_payment_date}</span>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleDeleteCard(card.id)}
                      variant="ghost"
                      size="icon"
                      className="text-[#FF007F] hover:bg-[#FF007F]/10"
                      data-testid={`delete-card-${card.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Creditors Section */}
          <section className="premium-card rounded-2xl p-6" data-testid="creditors-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#00F0FF]" />
                Other Creditors
              </h2>
              <Button
                onClick={() => setShowCreditorForm(!showCreditorForm)}
                className="bg-[#00FF66] text-black font-bold hover:bg-[#00FF66]/90"
                data-testid="add-creditor-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Creditor
              </Button>
            </div>

            {/* Add Creditor Form */}
            {showCreditorForm && (
              <form onSubmit={handleAddCreditor} className="bg-black/30 rounded-xl p-4 mb-6 space-y-4" data-testid="creditor-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white text-sm">Creditor Name</Label>
                    <Input
                      value={creditorForm.creditor_name}
                      onChange={(e) => setCreditorForm({ ...creditorForm, creditor_name: e.target.value })}
                      placeholder="e.g., ABC Collections"
                      className="input-dark mt-1"
                      required
                      data-testid="creditor-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-sm">Amount Owed</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                      <Input
                        type="number"
                        step="0.01"
                        value={creditorForm.amount_owed}
                        onChange={(e) => setCreditorForm({ ...creditorForm, amount_owed: e.target.value })}
                        placeholder="0.00"
                        className="pl-8 input-dark"
                        required
                        data-testid="creditor-amount-input"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white text-sm">Account Number (Optional)</Label>
                    <Input
                      value={creditorForm.account_number}
                      onChange={(e) => setCreditorForm({ ...creditorForm, account_number: e.target.value })}
                      placeholder="Account #"
                      className="input-dark mt-1"
                      data-testid="creditor-account-input"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-sm">Creditor Type</Label>
                    <Select value={creditorForm.creditor_type} onValueChange={(v) => setCreditorForm({ ...creditorForm, creditor_type: v })}>
                      <SelectTrigger className="input-dark mt-1" data-testid="creditor-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {CREDITOR_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="bg-[#00FF66] text-black font-bold" data-testid="save-creditor-btn">
                    Save Creditor
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowCreditorForm(false)} className="border-white/20 text-white">
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* Creditors List */}
            <div className="space-y-3">
              {creditors.length === 0 ? (
                <p className="text-center text-[#71717A] py-8">No creditors added yet</p>
              ) : (
                creditors.map((creditor) => (
                  <div key={creditor.id} className="bg-black/30 rounded-xl p-4 flex items-center justify-between" data-testid={`creditor-item-${creditor.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-[#00F0FF]" />
                        <span className="font-semibold text-white">{creditor.creditor_name}</span>
                        {creditor.creditor_type && (
                          <span className="text-xs px-2 py-0.5 bg-white/10 rounded text-[#A1A1AA]">{creditor.creditor_type}</span>
                        )}
                      </div>
                      <div className="flex gap-4 text-sm">
                        <span className="text-[#FF007F]">Owed: ${creditor.amount_owed?.toLocaleString()}</span>
                        {creditor.account_number && (
                          <span className="text-[#71717A]">Account: {creditor.account_number}</span>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleDeleteCreditor(creditor.id)}
                      variant="ghost"
                      size="icon"
                      className="text-[#FF007F] hover:bg-[#FF007F]/10"
                      data-testid={`delete-creditor-${creditor.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>

        {/* Credit Report Upload */}
        <section className="premium-card rounded-2xl p-6 mt-8" data-testid="credit-reports-section">
          <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
            <FileText className="w-5 h-5 text-[#FF007F]" />
            Credit Reports
          </h2>

          {/* Upload Zone */}
          <label className="upload-zone block cursor-pointer mb-6" data-testid="upload-zone">
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
              data-testid="file-input"
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <div className="spinner"></div>
                <p className="text-[#A1A1AA]">Uploading...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-[#FF007F]/20 flex items-center justify-center">
                  <Upload className="w-8 h-8 text-[#FF007F]" />
                </div>
                <div>
                  <p className="text-white font-semibold">Upload Credit Report</p>
                  <p className="text-sm text-[#71717A]">PDF files only • Click or drag to upload</p>
                </div>
              </div>
            )}
          </label>

          {/* Reports List */}
          <div className="space-y-3">
            {creditReports.length === 0 ? (
              <p className="text-center text-[#71717A] py-4">No credit reports uploaded yet</p>
            ) : (
              creditReports.map((report) => (
                <div key={report.id} className="bg-black/30 rounded-xl p-4 flex items-center justify-between" data-testid={`report-item-${report.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#FF007F]/20 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#FF007F]" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">{report.filename}</p>
                      <p className="text-sm text-[#71717A]">
                        Uploaded {new Date(report.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleDeleteReport(report.id)}
                    variant="ghost"
                    size="icon"
                    className="text-[#FF007F] hover:bg-[#FF007F]/10"
                    data-testid={`delete-report-${report.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Contact Info */}
        <div className="mt-8 text-center">
          <p className="text-[#71717A] mb-2">Need Help? Contact Us</p>
          <div className="flex justify-center gap-6">
            <a href="tel:18888714928" className="flex items-center gap-2 text-[#FFD700] hover:underline">
              <Phone className="w-4 h-4" />
              1 (888) 871-4928
            </a>
            <a href="mailto:cash@credit2cash.store" className="flex items-center gap-2 text-[#00FF66] hover:underline">
              <Mail className="w-4 h-4" />
              cash@credit2cash.store
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
